import React, { useState, useEffect, useCallback } from 'react';
import API_BASE from '../config';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmActionModal from '../components/ConfirmActionModal';

export default function Workspaces() {
  const { id } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [accountId, setAccountId] = useState(null);
  const [potBalance, setPotBalance] = useState({ pot: 0, topup: 0, total: 0, monthly_allocation: 0 });
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allocateModal, setAllocateModal] = useState(null);
  const [allocateAmount, setAllocateAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newWorkspace, setNewWorkspace] = useState({ name: '', sector: '', website: '' });
  const [addBusy, setAddBusy] = useState(false);
  const [statusModal, setStatusModal] = useState(null); // { workspace, newStatus }
  const [statusBusy, setStatusBusy] = useState(false);
  const [editModal, setEditModal] = useState(null); // workspace being edited
  const [editFields, setEditFields] = useState({ name: '', sector: '', website: '' });
  const [editBusy, setEditBusy] = useState(false);

  const authedFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    return fetch(API_BASE + path, {
      ...opts,
      headers: { ...(opts.headers || {}), 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    });
  }, [getToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const wsRes = await authedFetch('/clients/' + id);
      if (!wsRes.ok) throw new Error('Could not load workspace');
      const ws = await wsRes.json();
      const accId = ws.account_id;
      setAccountId(accId);
      const creditsRes = await authedFetch('/accounts/' + accId + '/credits');
      if (!creditsRes.ok) throw new Error('Could not load credits');
      const data = await creditsRes.json();
      setPotBalance(data.account);
      setWorkspaces(data.workspaces || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [authedFetch, id]);

  useEffect(() => { load(); }, [load]);

  async function submitAllocation() {
    if (!allocateModal) return;
    const amt = parseInt(allocateAmount, 10);
    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
    setSubmitting(true);
    try {
      const endpoint = allocateModal.mode === 'allocate' ? '/allocate' : '/deallocate';
      const res = await authedFetch('/accounts/' + accountId + endpoint, {
        method: 'POST',
        body: JSON.stringify({ workspace_id: allocateModal.workspace.id, amount: amt }),
      });
      const j = await res.json();
      if (!res.ok || !j.success) {
        toast.error(j.reason === 'insufficient_pot' ? 'Not enough credits in pot' : (j.reason || j.error || 'Failed'));
      } else {
        toast.success(allocateModal.mode === 'allocate' ? 'Allocated ' + amt + ' credits' : 'Returned ' + amt + ' credits to pot');
        setAllocateModal(null);
        setAllocateAmount('');
        await load();
        try { window.dispatchEvent(new CustomEvent('ce:creditsChanged')); } catch (_) {}
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function createWorkspace() {
    if (!newWorkspace.name.trim()) { toast.error('Workspace name required'); return; }
    setAddBusy(true);
    try {
      const res = await authedFetch('/clients', {
        method: 'POST',
        body: JSON.stringify({ ...newWorkspace, account_id: accountId, status: 'active' }),
      });
      if (!res.ok) {
        const j = await res.json();
        toast.error(j.error || 'Failed to create workspace');
      } else {
        toast.success('Workspace created');
        setAddOpen(false);
        setNewWorkspace({ name: '', sector: '', website: '' });
        await load();
        try { window.dispatchEvent(new CustomEvent('ce:creditsChanged')); } catch (_) {}
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddBusy(false);
    }
  }

  async function toggleStatus() {
    if (!statusModal) return;
    setStatusBusy(true);
    try {
      const res = await authedFetch('/clients/' + statusModal.workspace.id + '/status', {
        method: 'PATCH',
        body: JSON.stringify({ status: statusModal.newStatus }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.error || 'Failed');
      } else {
        toast.success(statusModal.newStatus === 'inactive' ? 'Workspace disabled' : 'Workspace reactivated');
        setStatusModal(null);
        await load();
        try { window.dispatchEvent(new CustomEvent('ce:creditsChanged')); } catch (_) {}
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setStatusBusy(false);
    }
  }

  async function submitEdit() {
    if (!editModal) return;
    if (!editFields.name.trim()) { toast.error('Name required'); return; }
    setEditBusy(true);
    try {
      const res = await authedFetch('/clients/' + editModal.id, {
        method: 'PUT',
        body: JSON.stringify({ name: editFields.name, sector: editFields.sector, website: editFields.website }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.error || 'Failed to update');
      } else {
        toast.success('Workspace updated');
        setEditModal(null);
        await load();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEditBusy(false);
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Loading...</div>;
  }

  const totalAvailable = (potBalance.pot || 0) + (potBalance.topup || 0);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.75rem' }}>
        <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: '0.4rem' }}>Account pot</div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1A1A1A' }}>{(potBalance.pot || 0).toLocaleString('en-GB')}</div>
          <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.3rem' }}>monthly allocation: {(potBalance.monthly_allocation || 0).toLocaleString('en-GB')}</div>
        </div>
        <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: '0.4rem' }}>Top-up balance</div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1A1A1A' }}>{(potBalance.topup || 0).toLocaleString('en-GB')}</div>
          <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.3rem' }}>rolls over month-to-month</div>
        </div>
        <div style={{ background: '#8B1A1A', borderRadius: '10px', padding: '1.25rem', color: '#fff' }}>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: '0.4rem' }}>Available to allocate</div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>{totalAvailable.toLocaleString('en-GB')}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1A1A1A' }}>Workspaces</h2>
        <button onClick={() => setAddOpen(true)} style={{ background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.55rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
          + Add workspace
        </button>
      </div>

      <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
        {workspaces.length === 0 && (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: '#888', fontSize: '0.88rem' }}>No workspaces yet.</div>
        )}
        {workspaces.map((w, i) => (
          <div key={w.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.95rem 1.25rem', borderTop: i === 0 ? 'none' : '0.5px solid #F0EDEA' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.92rem', fontWeight: 600, color: '#1A1A1A', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {w.name}
                {w.status === 'inactive' && <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#B7791F', background: '#FFFBEB', padding: '0.1rem 0.45rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>inactive</span>}
              </div>
              <div style={{ fontSize: '0.74rem', color: '#888', marginTop: '0.2rem' }}>
                {(w.credits_remaining || 0).toLocaleString('en-GB')} credits allocated
                {w.service_track === 'managed_service' && <span style={{ marginLeft: '0.5rem', color: '#276749' }}>· managed service (unlimited)</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button onClick={() => navigate('/clients/' + w.id)} style={{ background: '#fff', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.85rem', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                Open
              </button>
              <button onClick={() => { setEditFields({ name: w.name || '', sector: w.sector || '', website: w.website || '' }); setEditModal(w); }} style={{ background: '#fff', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.85rem', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                Edit
              </button>
              {w.status === 'inactive'
                ? <button onClick={() => setStatusModal({ workspace: w, newStatus: 'active' })} style={{ background: '#fff', color: '#276749', border: '1px solid #276749', borderRadius: '6px', padding: '0.4rem 0.85rem', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>Reactivate</button>
                : <button onClick={() => setStatusModal({ workspace: w, newStatus: 'inactive' })} style={{ background: '#fff', color: '#B7791F', border: '1px solid #B7791F', borderRadius: '6px', padding: '0.4rem 0.85rem', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>Disable</button>
              }
              {w.service_track !== 'managed_service' && (
                <>
                  <button onClick={() => { setAllocateModal({ workspace: w, mode: 'allocate' }); setAllocateAmount(''); }} style={{ background: '#fff', color: '#8B1A1A', border: '1px solid #8B1A1A', borderRadius: '6px', padding: '0.4rem 0.85rem', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                    Allocate
                  </button>
                  <button onClick={() => { setAllocateModal({ workspace: w, mode: 'deallocate' }); setAllocateAmount(''); }} disabled={!w.credits_remaining} style={{ background: '#fff', color: w.credits_remaining ? '#4A5568' : '#CCC', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.85rem', fontSize: '0.78rem', fontWeight: 500, cursor: w.credits_remaining ? 'pointer' : 'not-allowed' }}>
                    Return
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {allocateModal && (
        <div onClick={() => setAllocateModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '90%' }}>
            <h2 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: 700, color: '#1A1A1A' }}>
              {allocateModal.mode === 'allocate' ? 'Allocate credits' : 'Return credits to pot'}
            </h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#666' }}>
              {allocateModal.mode === 'allocate'
                ? <>Move credits from your account pot into <strong>{allocateModal.workspace.name}</strong>. They can be returned later.</>
                : <>Return credits from <strong>{allocateModal.workspace.name}</strong> back to your account pot.</>}
            </p>
            <div style={{ background: '#FAFAF8', borderRadius: '8px', padding: '0.7rem 0.9rem', marginBottom: '1rem', fontSize: '0.78rem', color: '#666' }}>
              {allocateModal.mode === 'allocate'
                ? <>Pot available: <strong>{totalAvailable.toLocaleString('en-GB')}</strong></>
                : <>Workspace has: <strong>{(allocateModal.workspace.credits_remaining || 0).toLocaleString('en-GB')}</strong></>}
            </div>
            <input
              type="number"
              value={allocateAmount}
              onChange={e => setAllocateAmount(e.target.value)}
              placeholder="Amount"
              style={{ width: '100%', padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '1px solid #D4CFC9', borderRadius: '6px', boxSizing: 'border-box', marginBottom: '1rem' }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '0.55rem' }}>
              <button onClick={() => setAllocateModal(null)} style={{ flex: 1, padding: '0.7rem', fontSize: '0.85rem', background: '#fff', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitAllocation} disabled={submitting} style={{ flex: 1, padding: '0.7rem', fontSize: '0.85rem', fontWeight: 600, background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', cursor: submitting ? 'not-allowed' : 'pointer' }}>
                {submitting ? 'Working...' : (allocateModal.mode === 'allocate' ? 'Allocate' : 'Return')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmActionModal
        open={!!statusModal}
        title={statusModal?.newStatus === 'inactive' ? 'Disable workspace?' : 'Reactivate workspace?'}
        body={statusModal?.newStatus === 'inactive'
          ? 'Disabling ' + (statusModal?.workspace?.name || '') + ' will prevent users from accessing it. Allocated credits stay with the workspace until you return them to your pot. You can reactivate any time.'
          : 'Reactivate ' + (statusModal?.workspace?.name || '') + '? Users will regain access.'}
        confirmLabel={statusModal?.newStatus === 'inactive' ? 'Disable' : 'Reactivate'}
        danger={statusModal?.newStatus === 'inactive'}
        onConfirm={toggleStatus}
        onCancel={() => !statusBusy && setStatusModal(null)}
      />

      {editModal && (
        <div onClick={() => !editBusy && setEditModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '460px', width: '90%' }}>
            <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem', fontWeight: 700, color: '#1A1A1A' }}>Edit workspace</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
              <input value={editFields.name} onChange={e => setEditFields({ ...editFields, name: e.target.value })} placeholder="Workspace name" style={{ padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '1px solid #D4CFC9', borderRadius: '6px' }} autoFocus />
              <input value={editFields.sector} onChange={e => setEditFields({ ...editFields, sector: e.target.value })} placeholder="Sector (optional)" style={{ padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '1px solid #D4CFC9', borderRadius: '6px' }} />
              <input value={editFields.website} onChange={e => setEditFields({ ...editFields, website: e.target.value })} placeholder="Website (optional)" style={{ padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '1px solid #D4CFC9', borderRadius: '6px' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.55rem' }}>
              <button onClick={() => setEditModal(null)} disabled={editBusy} style={{ flex: 1, padding: '0.7rem', fontSize: '0.85rem', background: '#fff', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitEdit} disabled={editBusy} style={{ flex: 1, padding: '0.7rem', fontSize: '0.85rem', fontWeight: 600, background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', cursor: editBusy ? 'not-allowed' : 'pointer' }}>
                {editBusy ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {addOpen && (
        <div onClick={() => setAddOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '460px', width: '90%' }}>
            <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem', fontWeight: 700, color: '#1A1A1A' }}>Add workspace</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
              <input value={newWorkspace.name} onChange={e => setNewWorkspace({ ...newWorkspace, name: e.target.value })} placeholder="Workspace name" style={{ padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '1px solid #D4CFC9', borderRadius: '6px' }} autoFocus />
              <input value={newWorkspace.sector} onChange={e => setNewWorkspace({ ...newWorkspace, sector: e.target.value })} placeholder="Sector (optional)" style={{ padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '1px solid #D4CFC9', borderRadius: '6px' }} />
              <input value={newWorkspace.website} onChange={e => setNewWorkspace({ ...newWorkspace, website: e.target.value })} placeholder="Website (optional)" style={{ padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '1px solid #D4CFC9', borderRadius: '6px' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.55rem' }}>
              <button onClick={() => setAddOpen(false)} style={{ flex: 1, padding: '0.7rem', fontSize: '0.85rem', background: '#fff', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={createWorkspace} disabled={addBusy} style={{ flex: 1, padding: '0.7rem', fontSize: '0.85rem', fontWeight: 600, background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', cursor: addBusy ? 'not-allowed' : 'pointer' }}>
                {addBusy ? 'Creating...' : 'Create workspace'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
